import React, { useState, useRef, useEffect, useMemo } from 'react';
import Card from './common/Card';
// FIX: Added PhotoIcon to imports to resolve module error.
import { ArrowLeftIcon, FileIcon, DocumentTextIcon, QuestionMarkCircleIcon, BoltIcon, PresentationChartBarIcon, PencilSquareIcon, TrashIcon, ClipboardDocumentListIcon, Square2StackIcon, PlayIcon, SparklesIcon, CheckBadgeIcon, UploadIcon, PuzzlePieceIcon, PhotoIcon, ArrowPathIcon, ArrowDownTrayIcon, ZoomInIcon, XMarkIcon, LinkIcon, ShareIcon, EyeIcon } from './common/Icons';
// FIX: Use relative path to resolve types module.
import { Document, LessonPlan, Quiz, Game, Presentation, View, Homework, Activity, ImageResource, UserSettings, VideoResource, VideoProject, Class, UploadTask, OralQuiz, Assignment, Student } from '../types';
import type { SKKNProject } from '../data/skknSampleData';
import { formatAssetUrl, fetchSharedWithMe, getShareLink } from '../services/apiService';
import { APP_MODE, FEATURE_FLAGS } from '../config';
import { getHomeworkLibraryBlurb, getHomeworkCompactCreateLabel } from '../utils/homeworkLabels';
import type { ScientificSimulation } from '../features/scientific-simulation/types';
import ConfirmationModal from './common/ConfirmationModal';
import { ShareModal } from './common/ShareModal';
import LoadingDataIndicator from './common/LoadingDataIndicator';
import { DownloadButtons } from './common/DownloadButtons';
import { LibrarySkeleton } from './common/Skeletons';
import { AssignHomeworkModal } from './common/AssignHomeworkModal';

type ItemToDelete = { id: string; name: string; type: 'document' | 'lesson' | 'quiz' | 'oralQuiz' | 'game' | 'presentation' | 'simulation' | 'homework' | 'activity' | 'image' | 'videoProject' | 'videoResource' | 'skkn' } | null;

type Tab = 'uploads' | 'images' | 'videoProjects' | 'videoResources' | 'lessons' | 'presentations' | 'simulations' | 'quizzes' | 'games' | 'homeworks' | 'activities' | 'skkn' | 'shared';

/** Tab mặc định theo vai trò (phụ huynh ưu tiên bài ôn tập cho con). */
function getDefaultLibraryTab(role: string | undefined): Tab {
    if (role === 'parent') return 'homeworks';
    if (role === 'teacher') return 'lessons';
    return 'uploads';
}

/** Thứ tự & lọc tab: phụ huynh ẩn SKKN + Kiểm tra (đề/trắc nghiệm GV), ưu tiên học liệu gia đình. */
function getVisibleLibraryTabs(role: string | undefined): Tab[] {
    const teacherStudentTabs: Tab[] = [
        'lessons', 'presentations', 'simulations', 'quizzes', 'games', 'homeworks', 'activities', 'skkn',
        'videoProjects', 'uploads', 'images', 'videoResources', 'shared',
    ];
    const parentTabs: Tab[] = [
        'homeworks', 'lessons', 'presentations', 'simulations', 'games', 'activities',
        'videoProjects', 'uploads', 'images', 'videoResources', 'shared',
    ];
    let tabs = role === 'parent' ? [...parentTabs] : [...teacherStudentTabs];
    if (FEATURE_FLAGS.VIDEO_GENERATION === false) {
        tabs = tabs.filter(t => t !== 'videoProjects');
    }
    return tabs;
}

function getDocumentLibraryTabLabel(tab: Tab, role: string | undefined): string {
    if (role === 'parent') {
        const m: Partial<Record<Tab, string>> = {
            lessons: 'Kế hoạch học',
            presentations: 'Trình chiếu',
            simulations: 'Mô phỏng',
            games: 'Trò chơi ôn tập',
            homeworks: 'Bài ôn & tập',
            activities: 'Hoạt động',
            videoProjects: 'Video học tập',
            uploads: 'Tài liệu',
            images: 'Hình ảnh',
            videoResources: 'Video',
            shared: 'Đã chia sẻ',
        };
        if (m[tab]) return m[tab]!;
    }
    const defaults: Record<Tab, string> = {
        lessons: 'Giáo án',
        presentations: 'Trình chiếu',
        simulations: 'Mô phỏng',
        quizzes: 'Kiểm tra',
        games: 'Trò chơi',
        homeworks: 'Bài tập',
        activities: 'Hoạt động',
        skkn: 'SKKN',
        videoProjects: 'Dự án Video',
        uploads: 'Tài liệu thô',
        images: 'Hình ảnh',
        videoResources: 'Video',
        shared: 'Đã chia sẻ',
    };
    return defaults[tab];
}

interface DocumentLibraryProps {
  onBack: () => void;
  onNavigate: (view: View, payload?: object) => void;
  user: UserSettings | null;
  classes?: Class[];
  documents: Document[];
  lessonPlans: LessonPlan[];
  quizzes: Quiz[];
  oralQuizzes: OralQuiz[];
  games: Game[];
  presentations: Presentation[];
  scientificSimulations: ScientificSimulation[];
  homeworks: Homework[];
  activities: Activity[];
  images: ImageResource[];
  videoProjects: VideoProject[];
  videoResources: VideoResource[];
  activeUploads?: UploadTask[];
  onDismissUpload?: (id: string) => void;
  isLoading?: boolean;
  onDeleteDocument: (id: string) => void;
  onUpdateDocumentName: (id: string, newName: string) => void;
  onDeleteLessonPlan: (id: string) => void;
  onUpdateLessonPlanName: (id: string, newName: string) => void;
  onDeleteQuiz: (id: string) => void;
  onUpdateQuizName: (id: string, newName: string) => void;
  onDeleteOralQuiz: (id: string) => void;
  onUpdateOralQuizName: (id: string, newName: string) => void;
  onDeleteGame: (id: string) => void;
  onUpdateGameName: (id: string, newName: string) => void;
  onDeletePresentation: (id: string) => void;
  onUpdatePresentationName: (id: string, newName: string) => void;
  onDeleteScientificSimulation: (id: string) => void;
  onUpdateScientificSimulationName: (id: string, newName: string) => void;
  onDeleteHomework: (id: string) => void;
  onUpdateHomeworkName: (id: string, newName: string) => void;
  onDeleteActivity: (id: string) => void;
  onUpdateActivityName: (id: string, newName: string) => void;
  onDeleteImage: (id: string) => void;
  onUpdateImageName: (id: string, newName: string) => void;
  onDeleteVideoProject: (id: string) => void;
  onUpdateVideoProjectName: (id: string, newName: string) => void;
  onDeleteVideoResource: (id: string) => void;
  onUpdateVideoResourceName: (id: string, newName: string) => void;
  onFileUploads: (files: File[]) => void;
  onImageUploads?: (files: File[]) => void;
  onVideoUploads?: (files: File[]) => void;
  onCombineDocuments: (docIds: string[], newName: string) => Promise<void>;
  onAssignHomework?: (data: Partial<Assignment>) => Promise<any>;
  students: Student[];
  skknProjects?: SKKNProject[];
  onDeleteSkkn?: (id: string | number) => void;
  onUpdateSkknName?: (id: string | number, newName: string) => void;
  onRefreshData?: () => void;
  onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
  activeTab?: Tab;
}

const UploadStatusList: React.FC<{ tasks: UploadTask[], onDismiss: (id: string) => void }> = ({ tasks, onDismiss }) => {
  if (tasks.length === 0) return null;

  return (
    <div className="mb-8 space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
          Tiến trình tải lên
        </h3>
        <span className="text-xs font-medium text-slate-400">{tasks.length} tệp</span>
      </div>
      {tasks.map(task => (
        <div key={task.id} className="bg-white/50 backdrop-blur-sm border border-slate-200/60 rounded-2xl p-4 shadow-sm hover:shadow-md hover:border-blue-200 transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4 min-w-0">
              <div className={`p-3 rounded-xl transition-all shadow-sm ${
                task.status === 'error' ? 'bg-red-50 text-red-500 shadow-red-100' : 
                task.status === 'completed' ? 'bg-emerald-50 text-emerald-500 shadow-emerald-100' : 
                'bg-blue-50 text-blue-600 shadow-blue-100'
              }`}>
                {task.type === 'image' ? <PhotoIcon className="w-5 h-5" /> : 
                 task.type === 'video' ? <PlayIcon className="w-5 h-5" /> : 
                 <FileIcon className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-800 truncate text-sm sm:text-base leading-tight mb-1">{task.fileName}</div>
                <div className={`text-xs font-bold tracking-tight ${
                  task.status === 'error' ? 'text-red-500' : 
                  task.status === 'completed' ? 'text-emerald-500' : 
                  'text-blue-500'
                }`}>
                  {task.status === 'uploading' ? `ĐANG XỬ LÝ: ${task.progress}%` : 
                   task.status === 'completed' ? 'TẢI LÊN THÀNH CÔNG' : 
                   `LỖI: ${task.error || 'DUNG LƯỢNG QUÁ LỚN HOẶC SAI ĐỊNH DẠNG'}`}
                </div>
              </div>
            </div>
            <button 
              onClick={() => onDismiss(task.id)} 
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
              title="Gỡ thông báo"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden shadow-inner">
            <div 
              className={`h-full transition-all duration-1000 ease-out ${
                task.status === 'error' ? 'bg-red-500' : 
                task.status === 'completed' ? 'bg-emerald-500' : 
                'bg-blue-600 shadow-[0_0_12px_rgba(37,99,235,0.5)]'
              }`}
              style={{ width: `${task.progress}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const TabButton: React.FC<{ activeTab: Tab; tabName: Tab; setTab: (tab: Tab) => void; children: React.ReactNode }> = ({ activeTab, tabName, setTab, children }) => (
    <button
        onClick={() => setTab(tabName)}
        className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
            activeTab === tabName
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }`}
    >
        {children}
    </button>
);

const ActionButtons: React.FC<{ 
    onEditClick?: () => void; 
    onDeleteClick?: () => void; 
    onShareClick?: () => void; 
    onCopyLink?: () => void; 
    onViewClick?: () => void;
    editTitle?: string; 
    children?: React.ReactNode 
}> = ({ onEditClick, onDeleteClick, onShareClick, onCopyLink, onViewClick, editTitle = "Sửa", children }) => (
    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {children}
        {onViewClick && (
            <button onClick={onViewClick} title="Xem chi tiết" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md">
                <EyeIcon className="h-5 w-5" />
            </button>
        )}
        {onCopyLink && (
            <button onClick={onCopyLink} title="Copy link chia sẻ" className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-md">
                <LinkIcon className="h-5 w-5" />
            </button>
        )}
        {onShareClick && (
            <button onClick={onShareClick} title="Thiết lập chia sẻ" className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded-md">
                <ShareIcon className="h-5 w-5" />
            </button>
        )}
        <div className="border-l h-5 mx-1 border-gray-200"></div>
        {onEditClick && (
            <button onClick={onEditClick} title={editTitle} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-md">
                <PencilSquareIcon className="h-5 w-5" />
            </button>
        )}
        {onDeleteClick && (
            <button onClick={onDeleteClick} title="Xóa" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-md">
                <TrashIcon className="h-5 w-5" />
            </button>
        )}
    </div>
);

const VisibilityBadge: React.FC<{ visibility?: string; isSharedWithMe?: boolean }> = ({ visibility, isSharedWithMe }) => {
    if (isSharedWithMe) return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full border border-blue-200">Được chia sẻ với tôi</span>;
    
    switch (visibility) {
        case 'public':
            return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full border border-green-200">Công khai</span>;
        case 'link':
            return <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-[10px] font-bold rounded-full border border-teal-200">Chia sẻ link</span>;
        case 'restricted':
            return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200">Giới hạn</span>;
        default:
            return <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] font-bold rounded-full border border-gray-200">Chỉ mình tôi</span>;
    }
};

const NoItemsMessage: React.FC<{ message: string }> = ({ message }) => (
    <div className="text-center py-12">
        <p className="text-gray-500">{message}</p>
    </div>
);

const UploadZone: React.FC<{ 
    onUpload: (files: File[]) => void; 
    accept: string; 
    title: string;
    description: string;
    icon?: React.ReactNode;
    onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}> = ({ onUpload, accept, title, description, icon, onShowToast }) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [dragCounter, setDragCounter] = useState(0);

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter(prev => prev + 1);
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragCounter(prev => {
            const newVal = prev - 1;
            if (newVal <= 0) {
                setIsDragging(false);
                return 0;
            }
            return newVal;
        });
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragging(true);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        setDragCounter(0);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        
        // Validation logic
        const fileArray = Array.from(files);
        const MAX_SIZE = 20 * 1024 * 1024; // 20MB limit for UI check, backend might be stricter per type
        
        const validFiles = fileArray.filter(file => {
            if (file.size > MAX_SIZE) {
                onShowToast?.(`Tệp ${file.name} quá lớn (tối đa 20MB).`, 'error');
                return false;
            }
            return true;
        });

        if (validFiles.length > 0) {
            onUpload(validFiles);
        }
    };

    return (
        <div 
            className={`mt-2 mb-6 border-2 border-dashed rounded-xl p-6 transition-all duration-200 flex flex-col items-center justify-center cursor-pointer
                ${isDragging ? 'border-teal-500 bg-teal-50 scale-[1.01]' : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
        >
            <input 
                type="file" 
                ref={fileInputRef} 
                onChange={(e) => handleFiles(e.target.files)} 
                multiple 
                className="hidden" 
                accept={accept} 
            />
            <div className="bg-teal-100 p-3 rounded-full mb-3 text-teal-600">
                {icon || <UploadIcon className="h-6 w-6" />}
            </div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500 mt-1">{description}</p>
            <p className="text-[10px] text-gray-400 mt-2 italic">Hỗ trợ kéo thả hoặc nhấn để chọn tệp</p>
        </div>
    );
};


const DocumentLibrary: React.FC<DocumentLibraryProps> = (props) => {
    const { user, onBack, onNavigate, onFileUploads, onImageUploads, onVideoUploads, onCombineDocuments, isLoading,
            onUpdateDocumentName, onUpdateLessonPlanName, onUpdatePresentationName,
            onUpdateQuizName, onUpdateGameName, onUpdateHomeworkName, onUpdateActivityName,
            onUpdateImageName, onUpdateVideoProjectName, onUpdateVideoResourceName,
            activeUploads = [], onDismissUpload
    } = props;
    const [activeTab, setActiveTab] = useState<Tab>(() => props.activeTab ?? getDefaultLibraryTab(user?.role));
    const [isCombineModalOpen, setIsCombineModalOpen] = useState(false);
    const [combineName, setCombineName] = useState('');
    const [isCombining, setIsCombining] = useState(false);

    const [itemToDelete, setItemToDelete] = useState<ItemToDelete>(null);
    const [shareModalItem, setShareModalItem] = useState<{ type: string; id: string; title: string; visibility?: string; shareToken?: string | null; shareScope?: { type: string; ids: number[] } | null; targetRoles?: string[] } | null>(null);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [assigningVideoProjectId, setAssigningVideoProjectId] = useState<string | null>(null);
    const [assigningVideoTitle, setAssigningVideoTitle] = useState('');
    const [sharedWithMe, setSharedWithMe] = useState<Record<string, any[]>>({});
    const [zoomedImage, setZoomedImage] = useState<ImageResource | null>(null);
    const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
    const [assessmentFilter, setAssessmentFilter] = useState<'all' | 'quiz' | 'knowledge'>('all');

    const visibleTabs = useMemo(() => getVisibleLibraryTabs(user?.role), [user?.role]);

    // Update activeTab when props.activeTab changes
    useEffect(() => {
        if (props.activeTab) {
            setActiveTab(props.activeTab);
        }
    }, [props.activeTab]);

    useEffect(() => {
        if (!visibleTabs.includes(activeTab)) {
            setActiveTab(visibleTabs[0] ?? getDefaultLibraryTab(user?.role));
        }
    }, [visibleTabs, activeTab, user?.role]);

    const handleCopyLink = (type: string, item: any) => {
        const title = item.title || item.name || item.topic || 'tai-nguyen';
        const token = (item as any).share_token || (item as any).shareToken;
        if (!token) return;
        const publicHtml5Url = (item as any).publicHtml5Url || (item as any).public_html5_url;
        const normalizedType = type === 'simulations' ? 'scientific-simulations' : type;
        const link = normalizedType === 'scientific-simulations' && publicHtml5Url
            ? `${publicHtml5Url}${String(publicHtml5Url).includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
            : getShareLink(normalizedType, item.id, title, token);
        navigator.clipboard.writeText(link);
        props.onShowToast?.('Đã copy link chia sẻ vào bộ nhớ tạm!', 'success');
    };

    const handleEditName = (id: string, currentName: string | undefined, type: NonNullable<ItemToDelete>['type']) => {
        if (!currentName) return;
        const newName = prompt(`Nhập tên mới cho "${currentName}":`, currentName);
        if (newName && newName.trim() !== currentName) {
             const updateFuncs: Record<NonNullable<ItemToDelete>['type'], ((id: string, newName: string) => void) | ((id: string | number, newName: string) => void) | undefined> = {
                document: onUpdateDocumentName,
                lesson: onUpdateLessonPlanName,
                presentation: onUpdatePresentationName,
                quiz: onUpdateQuizName,
                oralQuiz: props.onUpdateOralQuizName,
                game: onUpdateGameName,
                homework: onUpdateHomeworkName,
                activity: onUpdateActivityName,
                simulation: props.onUpdateScientificSimulationName,
                image: onUpdateImageName,
                videoProject: onUpdateVideoProjectName,
                videoResource: onUpdateVideoResourceName,
                skkn: props.onUpdateSkknName,
            };
            const fn = updateFuncs[type];
            if (fn) fn(id, newName.trim());
        }
    };
    
    const handleConfirmDelete = () => {
        if (!itemToDelete) return;

        switch (itemToDelete.type) {
            case 'document': props.onDeleteDocument(itemToDelete.id); break;
            case 'lesson': props.onDeleteLessonPlan(itemToDelete.id); break;
            case 'quiz': props.onDeleteQuiz(itemToDelete.id); break;
            case 'oralQuiz': props.onDeleteOralQuiz(itemToDelete.id); break;
            case 'game': props.onDeleteGame(itemToDelete.id); break;
            case 'presentation': props.onDeletePresentation(itemToDelete.id); break;
            case 'simulation': props.onDeleteScientificSimulation(itemToDelete.id); break;
            case 'homework': props.onDeleteHomework(itemToDelete.id); break;
            case 'activity': props.onDeleteActivity(itemToDelete.id); break;
            case 'image': props.onDeleteImage(itemToDelete.id); break;
            case 'videoProject': props.onDeleteVideoProject(itemToDelete.id); break;
            case 'videoResource': props.onDeleteVideoResource(itemToDelete.id); break;
            case 'skkn': props.onDeleteSkkn?.(itemToDelete.id); break;
        }
        setItemToDelete(null); // Close modal
    };
    
    useEffect(() => {
        if (APP_MODE === 'PRODUCTION' && user?.email) {
            fetchSharedWithMe().then(setSharedWithMe).catch(() => setSharedWithMe({}));
        }
    }, [user?.email]);

    const mergeWithShared = (key: string, owned: any[]) => {
        const keyMap: Record<string, string> = { lessonPlans: 'lesson-plans', presentations: 'presentations', scientificSimulations: 'scientific-simulations', quizzes: 'quizzes', games: 'games', homeworks: 'homeworks', activities: 'activities' };
        const apiKey = keyMap[key] || key;
        const shared = sharedWithMe[apiKey] || [];
        const ownedIds = new Set((owned || []).map((i: any) => String(i.id)));
        const extra = shared.filter((s: any) => !ownedIds.has(String(s.id))).map((s: any) => ({ ...s, id: String(s.id), sharedWithMe: true }));
        return [...(owned || []), ...extra];
    };

    const handleCombineClick = async () => {
        if (selectedDocIds.length < 2) {
            props.onShowToast?.("Vui lòng chọn ít nhất hai tài liệu.", 'info');
            return;
        }
        setCombineName(`Tổng hợp - ${new Date().toLocaleDateString('vi-VN')}`);
        setIsCombineModalOpen(true);
    };

    const handleConfirmCombine = async () => {
        if (combineName && combineName.trim()) {
            setIsCombining(true);
            try {
                await onCombineDocuments(selectedDocIds, combineName.trim());
                setSelectedDocIds([]); // Deselect after combining
                setIsCombineModalOpen(false);
                props.onShowToast?.("Đã tổng hợp tài liệu thành công!", 'success');
            } catch (error) {
                console.error("Combine error:", error);
                props.onShowToast?.("Đã có lỗi xảy ra khi tổng hợp tài liệu.", 'error');
            } finally {
                setIsCombining(false);
            }
        }
    };

    const renderContent = () => {
        const handleShareClick = (type: string, id: string, title: string | undefined, visibility?: string, shareScope?: { type: string; ids: number[] } | null, targetRoles?: string[], shareToken?: string | null) => {
            const titleStr = typeof title === 'string' ? title : (title && typeof title === 'object' ? String((title as any).title ?? (title as any).name ?? '') : '');
            setShareModalItem({ type, id, title: titleStr, visibility, shareScope, targetRoles, shareToken });
        };

        const commonListProps = { onNavigate, userSettings: user };

        // Helper to render actions for a specific tab
        const renderTabActions = () => {
            const r = user?.role;
            const isParent = r === 'parent';
            const canCreateLesson = r === 'teacher' || r === 'parent';
            const canCreateSimulation = r === 'teacher' || r === 'parent';
            const canCreateQuizOrOral = r === 'teacher';
            const canCreateGame = r === 'teacher' || r === 'parent';
            const canCreateActivity = r === 'teacher' || r === 'parent';
            const canCreateSkkn = r === 'teacher';
            const canCreateVideoProject = r === 'teacher' || r === 'parent';

            const createButton = (view: View, label: string, icon: React.ReactNode = <SparklesIcon className="h-5 w-5" />) => (
                <button 
                    onClick={() => onNavigate(view)}
                    className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
                >
                    {icon}
                    <span>{label}</span>
                </button>
            );

            switch (activeTab) {
                case 'uploads':
                    return <UploadZone 
                        onUpload={onFileUploads} 
                        accept=".pdf,.docx,.doc,.pptx,.txt" 
                        title={isParent ? 'Tải lên tài liệu' : 'Tải lên tài liệu thô'} 
                        description={isParent ? 'PDF, Word, PowerPoint hoặc văn bản để học cùng con (tối đa 10MB)' : 'PDF, Word, PowerPoint hoặc văn bản (tối đa 10MB)'} 
                        onShowToast={props.onShowToast}
                    />;
                case 'images':
                    return <UploadZone 
                        onUpload={onImageUploads || onFileUploads} 
                        accept="image/*" 
                        title="Tải lên hình ảnh" 
                        description={isParent ? 'Ảnh minh họa hoặc tài liệu quét (JPG, PNG…, tối đa 5MB)' : 'JPG, PNG, GIF, WEBP (tối đa 5MB)'} 
                        icon={<PhotoIcon className="h-6 w-6" />}
                        onShowToast={props.onShowToast}
                    />;
                case 'videoResources':
                    return <UploadZone 
                        onUpload={onVideoUploads || onFileUploads} 
                        accept="video/mp4,video/webm" 
                        title="Tải lên Video" 
                        description={isParent ? 'Video ôn tập hoặc minh họa (MP4, WEBM, tối đa 20MB)' : 'MP4, WEBM (tối đa 20MB)'} 
                        icon={<PlayIcon className="h-6 w-6" />}
                        onShowToast={props.onShowToast}
                    />;
                case 'lessons':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-blue-50 border border-blue-100 rounded-xl">
                            <DocumentTextIcon className="h-12 w-12 text-blue-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">{isParent ? 'Kế hoạch học cho con' : 'Quản lý Giáo án'}</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {isParent && canCreateLesson
                                    ? 'Soạn kế hoạch học hoặc bài học hỗ trợ con ôn tập ở nhà (không thay cho giáo án lớp học).'
                                    : !isParent && canCreateLesson
                                        ? 'Tạo giáo án chuyên nghiệp bằng AI hoặc từ tài liệu của bạn.'
                                        : 'Các bài đã lưu hoặc được chia sẻ với bạn.'}
                            </p>
                            {canCreateLesson && (
                                <div className="flex gap-3">
                                    {createButton(View.LessonPlanCreator, isParent ? '+ Soạn kế hoạch mới' : '+ Soạn Giáo án mới')}
                                </div>
                            )}
                        </div>
                    );
                case 'presentations':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-green-50 border border-green-100 rounded-xl">
                            <PresentationChartBarIcon className="h-12 w-12 text-green-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">{isParent ? 'Slide học tập' : 'Bài trình chiếu'}</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {isParent
                                    ? 'Tạo slide minh họa để con xem và ôn tập cùng gia đình.'
                                    : 'Thiết kế bài giảng sinh động với hình ảnh và nội dung AI.'}
                            </p>
                            <div className="flex gap-3">
                                {createButton(View.SlideCreator, isParent ? '+ Tạo slide' : '+ Tạo Trình chiếu')}
                            </div>
                        </div>
                    );
                case 'simulations':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-teal-50 border border-teal-100 rounded-xl">
                            <BoltIcon className="h-12 w-12 text-teal-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">Mô phỏng khoa học</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {canCreateSimulation
                                    ? (isParent
                                        ? 'Mô phỏng tương tác giúp con hình dung bài — tạo hoặc mở để học cùng con.'
                                        : 'Quản lý các mô phỏng tương tác để dạy học và chia sẻ nhanh cho người học.')
                                    : 'Mô phỏng đã lưu hoặc được chia sẻ với bạn (ôn tập trực quan).'}
                            </p>
                            {canCreateSimulation && (
                                <div className="flex flex-wrap gap-3 justify-center">
                                    {createButton(View.ScientificSimulationCreator, "+ Tạo Mô phỏng", <BoltIcon className="h-5 w-5" />)}
                                    <button
                                        type="button"
                                        onClick={() => onNavigate(View.ThietKe3DCreator)}
                                        className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                                    >
                                        <Square2StackIcon className="h-5 w-5" />
                                        <span>+ Thiết kế 3D</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                case 'quizzes':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-purple-50 border border-purple-100 rounded-xl">
                            <QuestionMarkCircleIcon className="h-12 w-12 text-purple-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">Thư viện Kiểm tra & ôn tập</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {canCreateQuizOrOral
                                    ? 'Quản lý chung đề kiểm tra và các phiên kiểm tra kiến thức nhanh trên lớp.'
                                    : 'Đề kiểm tra, bài ôn luyện và kiểm tra kiến thức đã lưu hoặc được chia sẻ với bạn.'}
                            </p>
                            {canCreateQuizOrOral && (
                                <div className="flex gap-3">
                                    {createButton(View.QuizCreator, "+ Tạo Bài kiểm tra")}
                                    {createButton(View.OralQuizCreator, "+ Tạo Kiểm tra kiến thức")}
                                </div>
                            )}
                            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                                {[
                                    { id: 'all', label: `Tất cả (${props.quizzes.length + props.oralQuizzes.length})` },
                                    { id: 'quiz', label: `Đề kiểm tra (${props.quizzes.length})` },
                                    { id: 'knowledge', label: `Kiểm tra kiến thức (${props.oralQuizzes.length})` },
                                ].map(option => (
                                    <button
                                        key={option.id}
                                        onClick={() => setAssessmentFilter(option.id as 'all' | 'quiz' | 'knowledge')}
                                        className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition-all ${assessmentFilter === option.id ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-500 border-slate-200 hover:border-purple-300'}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                case 'games':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-orange-50 border border-orange-100 rounded-xl">
                            <BoltIcon className="h-12 w-12 text-orange-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">{isParent ? 'Trò chơi ôn tập' : 'Trò chơi Giáo dục'}</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {canCreateGame
                                    ? (isParent ? 'Trò chơi giúp con ôn kiến thức nhẹ nhàng.' : 'Biến bài học thành trò chơi tương tác hấp dẫn.')
                                    : 'Trò chơi ôn tập đã lưu hoặc được chia sẻ với bạn.'}
                            </p>
                            {canCreateGame && (
                                <div className="flex gap-3">
                                    {createButton(View.GameCreator, isParent ? '+ Tạo trò chơi ôn tập' : '+ Tạo Trò chơi', <PuzzlePieceIcon className="h-5 w-5" />)}
                                </div>
                            )}
                        </div>
                    );
                case 'homeworks':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-yellow-50 border border-yellow-100 rounded-xl">
                            <ClipboardDocumentListIcon className="h-12 w-12 text-yellow-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">{isParent ? 'Bài ôn tập cho con' : 'Bài tập & Nhiệm vụ'}</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">{getHomeworkLibraryBlurb(user?.role)}</p>
                            <div className="flex gap-3">
                                {createButton(View.HomeworkCreator, getHomeworkCompactCreateLabel(user?.role))}
                            </div>
                        </div>
                    );
                case 'activities':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-indigo-50 border border-indigo-100 rounded-xl">
                            <PuzzlePieceIcon className="h-12 w-12 text-indigo-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">Hoạt động Sáng tạo</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {canCreateActivity
                                    ? (isParent ? 'Kịch bản, bài hát, hoạt động vui cho con học cùng gia đình.' : 'Kịch bản video, lời bài hát, vần thơ... giúp bài giảng thêm thú vị.')
                                    : 'Hoạt động sáng tạo đã lưu hoặc được chia sẻ với bạn.'}
                            </p>
                            {canCreateActivity && (
                                <div className="flex gap-3">
                                    {createButton(View.ActivityCreator, "+ Tạo Hoạt động")}
                                </div>
                            )}
                        </div>
                    );
                case 'skkn':
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-amber-50 border border-amber-100 rounded-xl">
                            <CheckBadgeIcon className="h-12 w-12 text-amber-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">Sáng kiến kinh nghiệm</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {canCreateSkkn
                                    ? 'Hỗ trợ viết SKKN theo cấu trúc chuẩn, khoa học.'
                                    : 'SKKN do giáo viên soạn hoặc được chia sẻ với bạn.'}
                            </p>
                            {canCreateSkkn && (
                                <div className="flex gap-3">
                                    {createButton(View.SKKNCreator, "+ Viết SKKN mới")}
                                </div>
                            )}
                        </div>
                    );
                case 'videoProjects':
                    if (FEATURE_FLAGS.VIDEO_GENERATION === false) return null;
                    return (
                        <div className="mb-6 flex flex-col items-center p-8 bg-teal-50 border border-teal-100 rounded-xl">
                            <PlayIcon className="h-12 w-12 text-teal-500 mb-3" />
                            <h3 className="text-lg font-bold text-gray-900">{isParent ? 'Video học tập' : 'Dự án Video'}</h3>
                            <p className="text-sm text-gray-600 mb-4 text-center max-w-md">
                                {canCreateVideoProject
                                    ? (isParent ? 'Video minh họa hoặc ôn tập cho con, tạo bằng AI.' : 'Tạo video bài giảng chuyên nghiệp bằng AI.')
                                    : 'Video đã lưu hoặc được chia sẻ với bạn.'}
                            </p>
                            {canCreateVideoProject && (
                                <div className="flex gap-3">
                                    {createButton(View.VideoCreator, isParent ? '+ Tạo video' : '+ Tạo Video Project')}
                                </div>
                            )}
                        </div>
                    );
                default:
                    return null;
            }
        };

        const wrapWithActions = (content: React.ReactNode) => (
            <>
                <UploadStatusList 
                    tasks={activeUploads.filter(t => {
                        if (activeTab === 'uploads') return t.type === 'document';
                        if (activeTab === 'images') return t.type === 'image';
                        if (activeTab === 'videoResources') return t.type === 'video';
                        return false;
                    })} 
                    onDismiss={(id) => onDismissUpload?.(id)} 
                />
                {renderTabActions()}
                {content}
            </>
        );

        // Tab-specific data check
        const tabDataMap: Record<Tab, any[]> = {
            uploads: props.documents,
            images: props.images,
            videoProjects: props.videoProjects,
            videoResources: props.videoResources,
            lessons: mergeWithShared('lessonPlans', props.lessonPlans),
            presentations: mergeWithShared('presentations', props.presentations),
            simulations: mergeWithShared('scientificSimulations', props.scientificSimulations),
            quizzes: [...mergeWithShared('quizzes', props.quizzes), ...(props.oralQuizzes || [])],
            games: mergeWithShared('games', props.games),
            homeworks: mergeWithShared('homeworks', props.homeworks),
            activities: mergeWithShared('activities', props.activities),
            skkn: props.skknProjects || [],
            shared: [] // We'll handle this specially
        };

        if (activeTab === 'shared') {
            const allShared = [
                ...(props.lessonPlans || []).map(p => ({ ...p, tabType: 'lessons' })),
                ...(props.presentations || []).map(p => ({ ...p, tabType: 'presentations' })),
                ...(props.scientificSimulations || []).map(p => ({ ...p, tabType: 'simulations' })),
                ...(props.quizzes || []).map(p => ({ ...p, tabType: 'quizzes' })),
                ...(props.games || []).map(p => ({ ...p, tabType: 'games' })),
                ...(props.homeworks || []).map(p => ({ ...p, tabType: 'homeworks' })),
                ...(props.activities || []).map(p => ({ ...p, tabType: 'activities' })),
                ...(props.documents || []).map(p => ({ ...p, tabType: 'uploads' })),
            ].filter(item => (item as any).visibility && (item as any).visibility !== 'private');

            if (allShared.length === 0) return <NoItemsMessage message="Bạn chưa chia sẻ tài nguyên nào." />;
            
            return (
                <div className="space-y-4">
                    <div className="bg-teal-50 p-4 rounded-xl border border-teal-100 mb-6">
                        <p className="text-sm text-teal-800 font-medium">
                            Đây là nơi bạn quản lý tất cả các tài nguyên đã chia sẻ. Bạn có thể nhanh chóng lấy link hoặc thay đổi quyền truy cập.
                        </p>
                    </div>
                    <ul className="divide-y divide-gray-200">
                        {allShared.map((item: any) => {
                            const type = item.tabType;
                            const sharedSavedAt = item.savedDate || item.uploadDate || item.saved_date || item.updatedAt || item.updated_at || item.createdAt || item.created_at;
                            return (
                                <li key={`${type}-${item.id}`} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                                    <div className="flex items-center space-x-4">
                                        {type === 'lessons' && <DocumentTextIcon className="h-8 w-8 text-blue-500" />}
                                        {type === 'presentations' && <PresentationChartBarIcon className="h-8 w-8 text-green-500" />}
                                        {type === 'simulations' && <BoltIcon className="h-8 w-8 text-teal-500" />}
                                        {type === 'quizzes' && <QuestionMarkCircleIcon className="h-8 w-8 text-purple-500" />}
                                        {type === 'games' && <BoltIcon className="h-8 w-8 text-orange-500" />}
                                        {type === 'homeworks' && <ClipboardDocumentListIcon className="h-8 w-8 text-yellow-500" />}
                                        {type === 'activities' && <PuzzlePieceIcon className="h-8 w-8 text-indigo-500" />}
                                        {type === 'uploads' && <FileIcon type={item.type} className="h-8 w-8" />}
                                        <div>
                                            <div className="flex items-center space-x-2">
                                                <p className="font-medium text-gray-800">{item.title || item.name || item.topic}</p>
                                                <VisibilityBadge visibility={item.visibility} />
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                {type === 'uploads' ? 'Tài liệu' : 
                                                 type === 'lessons' ? 'Giáo án' :
                                                 type === 'presentations' ? 'Trình chiếu' :
                                                 type === 'simulations' ? 'Mô phỏng khoa học' :
                                                 type === 'quizzes' ? 'Bài kiểm tra' :
                                                 type === 'games' ? 'Trò chơi' :
                                                 type === 'homeworks' ? 'Bài tập' : 'Hoạt động'}
                                                {' | '} 
                                                Lưu ngày: {sharedSavedAt ? new Date(sharedSavedAt).toLocaleString('vi-VN') : '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <ActionButtons 
                                            onEditClick={() => {
                                                const viewMap: Record<string, View> = {
                                                    lessons: View.LessonPlanEdit,
                                                    presentations: View.SlideEdit,
                                                    simulations: View.ScientificSimulationEdit,
                                                    quizzes: item.currentStep === 4 ? View.QuizView : View.QuizEdit,
                                                    games: View.GameEdit,
                                                    homeworks: View.HomeworkEdit,
                                                    activities: View.ActivityEdit,
                                                    uploads: View.DocumentWorkspace
                                                };
                                                const view = viewMap[type] || View.QuizEdit;
                                                const payload: any = { id: item.id, title: item.title || item.name || item.topic };
                                                
                                                // Đối với quiz, nếu chưa xong thì truyền step vào URL
                                                if (type === 'quizzes' && item.currentStep !== undefined && item.currentStep !== 4) {
                                                    payload.step = item.currentStep;
                                                }
                                                
                                                onNavigate(view, payload);
                                            }}
                                            onDeleteClick={() => setItemToDelete({ id: item.id, name: item.title || item.name, type: type === 'uploads' ? 'document' : type.slice(0, -1) as any })}
                                            onShareClick={() => handleShareClick(type === 'uploads' ? 'documents' : type, item.id, item.title || item.name, item.visibility, item.shareScope, item.target_roles, item.share_token || item.shareToken)}
                                            onCopyLink={() => handleCopyLink(type === 'uploads' ? 'documents' : type, item)}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            );
        }

        const currentTabData = tabDataMap[activeTab];

        if (isLoading && (!currentTabData || currentTabData.length === 0)) {
            return <LibrarySkeleton />;
        }

        switch (activeTab) {
            case 'uploads':
                return wrapWithActions(
                    <UploadedFilesList 
                        {...commonListProps}
                        files={props.documents} 
                        selectedDocIds={selectedDocIds}
                        setSelectedDocIds={setSelectedDocIds}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'document')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'document' })}
                        onShareClick={(id, name, item) => handleShareClick('documents', id, name || '', (item as any)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('documents', item)}
                    />
                );
            case 'images':
                return wrapWithActions(
                    <ImagesList 
                        images={props.images}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'image')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'image' })}
                        onZoom={(img) => setZoomedImage(img)}
                    />
                );
            case 'videoProjects':
                return wrapWithActions(
                    <VideoProjectsList
                        videoProjects={props.videoProjects}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'videoProject')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'videoProject' })}
                        onAssignClick={(projectId, name) => {
                            setAssigningVideoProjectId(String(projectId));
                            setAssigningVideoTitle(name);
                            setIsAssignModalOpen(true);
                        }}
                        {...commonListProps}
                    />
                );
            case 'videoResources':
                return wrapWithActions(
                    <VideoResourcesList
                        videos={props.videoResources}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'videoResource')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'videoResource' })}
                        {...commonListProps}
                    />
                );
            case 'lessons':
                return wrapWithActions(
                    <LessonPlansList 
                        {...commonListProps}
                        plans={mergeWithShared('lessonPlans', props.lessonPlans)} 
                        onEditClick={(id, name) => handleEditName(String(id), name, 'lesson')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'lesson' })}
                        onShareClick={(id, name, item) => handleShareClick('lessonPlans', id, name || '', (item as any)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('lessonPlans', item)}
                    />
                );
            case 'presentations':
                return wrapWithActions(
                    <PresentationsList 
                        {...commonListProps}
                        presentations={mergeWithShared('presentations', props.presentations)} 
                        onEditClick={(id, name) => handleEditName(String(id), name, 'presentation')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'presentation' })}
                        onShareClick={(id, name, item) => handleShareClick('presentations', id, name || '', (item as any)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('presentations', item)}
                    />
                );
            case 'simulations':
                return wrapWithActions(
                    <ScientificSimulationsList
                        {...commonListProps}
                        simulations={mergeWithShared('scientificSimulations', props.scientificSimulations)}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'simulation')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'simulation' })}
                        onShareClick={(id, name, item) => handleShareClick('scientific-simulations', id, name || '', (item as any)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('scientific-simulations', item)}
                    />
                );
            case 'quizzes':
                return wrapWithActions(
                    <QuizzesList 
                        {...commonListProps}
                        quizzes={mergeWithShared('quizzes', props.quizzes)} 
                        oralQuizzes={props.oralQuizzes || []}
                        filter={assessmentFilter}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'quiz')}
                        onEditOralQuizClick={(id, name) => handleEditName(String(id), name, 'oralQuiz')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'quiz' })}
                        onDeleteOralQuizClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'oralQuiz' })}
                        onShareClick={(id, name, item) => handleShareClick('quizzes', id, name || '', (item as any)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('quizzes', item)}
                    />
                );
            case 'games':
                return wrapWithActions(
                    <GamesList 
                        {...commonListProps}
                        games={mergeWithShared('games', props.games)} 
                        onEditClick={(id, name) => handleEditName(String(id), name, 'game')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'game' })}
                        onShareClick={(id, name, item) => handleShareClick('games', id, name || '', (item as Game)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('games', item)}
                    />
                );
            case 'homeworks':
                return wrapWithActions(
                    <HomeworksList 
                        {...commonListProps}
                        homeworks={mergeWithShared('homeworks', props.homeworks)} 
                        onEditClick={(id, name) => handleEditName(String(id), name, 'homework')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'homework' })}
                        onShareClick={(id, name, item) => handleShareClick('homeworks', id, name || '', (item as any)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('homeworks', item)}
                    />
                );
            case 'activities':
                return wrapWithActions(
                    <ActivitiesList 
                        {...commonListProps}
                        activities={mergeWithShared('activities', props.activities)}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'activity')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'activity' })}
                        onShareClick={(id, name, item) => handleShareClick('activities', id, name || '', (item as any)?.visibility, (item as any)?.shareScope, (item as any)?.target_roles, (item as any)?.share_token || (item as any)?.shareToken)}
                        onCopyLink={(item) => handleCopyLink('activities', item)}
                    />
                );
            case 'skkn':
                return wrapWithActions(
                    <SKKNList 
                        projects={props.skknProjects || []}
                        onEditClick={(id, name) => handleEditName(String(id), name, 'skkn')}
                        onDeleteClick={(id, name) => setItemToDelete({ id: String(id), name, type: 'skkn' })}
                        {...commonListProps} 
                    />
                );
            default:
                return null;
        }
    };

    const isGroupMember = !!user?.memberOfGroupId;
    const isGroupAdmin = (user?.groupMembers?.length ?? 0) > 0;
    const storageLimit = isGroupMember ? user?.groupStorageLimitMB : (isGroupAdmin ? user?.groupStorageLimitMB : user?.storageLimitMB);
    const storageUsed = isGroupMember ? user?.groupStorageUsedMB : (isGroupAdmin ? user?.groupStorageUsedMB : user?.storageUsedMB);
    const storageUsedPercentage = (storageLimit ?? 0) > 0 ? ((storageUsed ?? 0) / (storageLimit ?? 1)) * 100 : 0;


    const handleBackWithRole = () => {
        if (!user?.role) {
            onBack();
            return;
        }
        const dashboardMap: Record<string, View> = {
            'teacher': View.TeacherTools,
            'parent': View.ParentDashboard,
            'student': View.StudentDashboard
        };
        onNavigate(dashboardMap[user.role] || View.Dashboard);
    };

    return (
        <div 
            className="space-y-6 relative"
        >
            {shareModalItem && (
                <ShareModal
                    isOpen={!!shareModalItem}
                    onClose={() => setShareModalItem(null)}
                    resourceType={shareModalItem.type}
                    resourceId={shareModalItem.id}
                    resourceTitle={shareModalItem.title}
                    currentVisibility={shareModalItem.visibility}
                    currentShareToken={shareModalItem.shareToken}
                    currentShareScope={shareModalItem.shareScope}
                    currentTargetRoles={shareModalItem.targetRoles}
                    classes={props.classes || []}
                    user={props.user}
                    onShowToast={props.onShowToast}
                    onUpdated={() => { props.onRefreshData?.(); }}
                />
            )}

            {isAssignModalOpen && assigningVideoProjectId && (
                <AssignHomeworkModal
                    isOpen={isAssignModalOpen}
                    onClose={() => {
                        setIsAssignModalOpen(false);
                        setAssigningVideoProjectId(null);
                    }}
                    homeworks={props.homeworks}
                    classStudents={props.students}
                    classes={props.classes || []}
                    user={props.user!}
                    onAssign={async (data) => {
                        if (props.onAssignHomework) {
                            try {
                                await props.onAssignHomework({
                                    ...data,
                                    videoProjectId: assigningVideoProjectId,
                                    type: 'task'
                                });
                                setIsAssignModalOpen(false);
                                setAssigningVideoProjectId(null);
                            } catch (err) {
                                // Error handled by App.tsx handleAssignHomework
                            }
                        }
                    }}
                    initialType="task"
                    resourceInfo={{
                        id: assigningVideoProjectId,
                        title: assigningVideoTitle,
                        type: 'video'
                    }}
                />
            )}
            <ConfirmationModal
                isOpen={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onConfirm={handleConfirmDelete}
                title={`Xác nhận xóa`}
                message={`Bạn có chắc chắn muốn xóa "${itemToDelete?.name}" không? Hành động này không thể hoàn tác.`}
            />

            {/* Combine Modal */}
            {isCombineModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => !isCombining && setIsCombineModalOpen(false)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100" onClick={e => e.stopPropagation()}>
                        <div className="p-8 text-center space-y-6">
                            <div className="mx-auto w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center">
                                {isCombining ? (
                                    <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
                                ) : (
                                    <Square2StackIcon className="h-8 w-8 text-blue-600" />
                                )}
                            </div>
                            
                            <div className="space-y-2">
                                <h3 className="text-xl font-black text-slate-900">
                                    {isCombining ? 'Đang tổng hợp dữ liệu...' : 'Tổng hợp tài liệu'}
                                </h3>
                                <p className="text-sm text-slate-500">
                                    {isCombining 
                                        ? 'Vui lòng chờ trong giây lát, AI đang xử lý và gộp các tài liệu của bạn.' 
                                        : `Đặt tên cho tài liệu mới được tổng hợp từ ${selectedDocIds.length} tệp đã chọn.`}
                                </p>
                            </div>

                            {!isCombining && (
                                <div className="text-left animate-in fade-in slide-in-from-top-2">
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Tên tài liệu mới</label>
                                    <input
                                        type="text"
                                        value={combineName}
                                        onChange={(e) => setCombineName(e.target.value)}
                                        placeholder="Nhập tên tài liệu..."
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium text-slate-700"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleConfirmCombine();
                                            if (e.key === 'Escape') setIsCombineModalOpen(false);
                                        }}
                                    />
                                </div>
                            )}

                            {isCombining && (
                                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-600 animate-infinite-progress rounded-full"></div>
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => setIsCombineModalOpen(false)}
                                    disabled={isCombining}
                                    className="flex-1 px-6 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    onClick={handleConfirmCombine}
                                    disabled={!combineName.trim() || isCombining}
                                    className="flex-[1.5] px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2"
                                >
                                    {isCombining && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                                    <span>{isCombining ? 'Đang xử lý...' : 'Bắt đầu tổng hợp'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {zoomedImage && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 transition-all duration-300"
                    onClick={() => setZoomedImage(null)}
                >
                    <button 
                        className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
                        onClick={() => setZoomedImage(null)}
                    >
                        <XMarkIcon className="h-8 w-8" />
                    </button>
                    <div className="max-w-full max-h-full relative group" onClick={e => e.stopPropagation()}>
                        <img 
                            src={formatAssetUrl(zoomedImage.url)} 
                            alt={zoomedImage.name} 
                            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                        />
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-lg font-bold">{zoomedImage.name}</p>
                            <p className="text-sm opacity-80">Nguồn: {zoomedImage.source}</p>
                            <div className="mt-2 flex gap-2">
                                <a 
                                    href={formatAssetUrl(zoomedImage.url)} 
                                    download={`${zoomedImage.name}.png`}
                                    className="px-4 py-1.5 bg-teal-600 text-white rounded-md text-sm font-semibold hover:bg-teal-700 transition-colors"
                                >
                                    Tải xuống
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-start">
                <div>
                    <button onClick={handleBackWithRole} className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900">
                        <ArrowLeftIcon className="h-5 w-5" />
                        <span>Quay lại bảng điều khiển</span>
                    </button>
                    <h2 className="text-2xl font-bold text-gray-900 mt-2">
                        {user?.role === 'parent' ? 'Thư viện gia đình' : 'Thư viện của tôi'}
                    </h2>
                    {user?.role === 'parent' && (
                        <p className="text-sm text-gray-500 mt-1 max-w-xl">
                            Học liệu và bài ôn cho con; tệp tải lên và nội dung được chia sẻ.
                        </p>
                    )}
                </div>
            </div>

            {user && (storageLimit ?? 0) > 0 && (
                <div className="p-4 bg-gray-50 border rounded-lg">
                    <div className="flex justify-between items-center text-sm mb-1">
                        <p className="font-semibold text-gray-700">
                            Dung lượng lưu trữ {isGroupMember || isGroupAdmin ? "(Nhóm)" : "(Cá nhân)"}
                        </p>
                        <p className="font-bold text-gray-800">
                            Đã dùng {(storageUsed ?? 0).toFixed(2)} MB / {((storageLimit ?? 0) / 1024).toFixed(2)} GB
                        </p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-teal-600 h-2.5 rounded-full" style={{ width: `${storageUsedPercentage}%` }}></div>
                    </div>
                </div>
            )}

            <Card className="overflow-hidden">
                 <div className="border-b border-gray-200 px-6 flex justify-between items-center">
                    <nav className="-mb-px flex space-x-1 sm:space-x-4 overflow-x-auto custom-scrollbar scroll-smooth" aria-label="Tabs">
                        {visibleTabs.map(tab => {
                            const tabCounts: Record<Tab, number> = {
                                lessons: props.lessonPlans.length,
                                presentations: props.presentations.length,
                                simulations: props.scientificSimulations.length,
                                quizzes: props.quizzes.length + props.oralQuizzes.length,
                                games: props.games.length,
                                homeworks: props.homeworks.length,
                                activities: props.activities.length,
                                skkn: (props.skknProjects || []).length,
                                videoProjects: props.videoProjects.length,
                                uploads: props.documents.length,
                                images: props.images.length,
                                videoResources: props.videoResources.length,
                                shared: [
                                    ...(props.lessonPlans || []),
                                    ...(props.presentations || []),
                                    ...(props.scientificSimulations || []),
                                    ...(props.quizzes || []),
                                    ...(props.games || []),
                                    ...(props.homeworks || []),
                                    ...(props.activities || []),
                                    ...(props.documents || []),
                                ].filter((item: any) => item.visibility && item.visibility !== 'private').length,
                            };
                            const label = getDocumentLibraryTabLabel(tab, user?.role);
                            const count = tabCounts[tab];
                            return (
                                <TabButton key={tab} activeTab={activeTab} tabName={tab} setTab={setActiveTab}>
                                    <span className="whitespace-nowrap">{label} <span className="ml-1 opacity-50 text-[10px]">({count})</span></span>
                                </TabButton>
                            );
                        })}
                    </nav>
                    {activeTab === 'uploads' && selectedDocIds.length > 0 && (
                        <div className="py-2">
                             <button
                                onClick={handleCombineClick}
                                disabled={selectedDocIds.length < 2}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                                title={selectedDocIds.length < 2 ? "Chọn ít nhất 2 tệp để tổng hợp" : "Tổng hợp các tệp đã chọn"}
                            >
                                <Square2StackIcon className="h-5 w-5" />
                                <span>Tổng hợp ({selectedDocIds.length})</span>
                            </button>
                        </div>
                    )}
                </div>
                <div className="p-6 min-h-[300px]">
                    {renderContent()}
                </div>
            </Card>
        </div>
    );
};

interface ListProps<T extends {id: string | number, title?: string, name?: string}> {
    onEditClick: (id: string | number, name: string | undefined) => void;
    onDeleteClick: (id: string | number, name: string | undefined) => void;
}

const FileTypeBadge: React.FC<{ type: string }> = ({ type }) => {
    const configs: Record<string, { label: string, bg: string, text: string }> = {
        pdf: { label: 'PDF', bg: 'bg-red-50', text: 'text-red-600' },
        docx: { label: 'DOCX', bg: 'bg-blue-50', text: 'text-blue-600' },
        doc: { label: 'DOC', bg: 'bg-blue-50', text: 'text-blue-600' },
        pptx: { label: 'PPTX', bg: 'bg-orange-50', text: 'text-orange-600' },
        txt: { label: 'TXT', bg: 'bg-slate-100', text: 'text-slate-600' },
    };
    const config = configs[type] || { label: type.toUpperCase(), bg: 'bg-gray-50', text: 'text-gray-600' };
    return (
        <span className={`px-1.5 py-0.5 ${config.bg} ${config.text} text-[9px] font-black rounded border border-current/10 w-9 text-center flex-shrink-0 tracking-tighter`}>
            {config.label}
        </span>
    );
};

const UploadedFilesList: React.FC<{
    files: Document[];
    selectedDocIds: string[];
    setSelectedDocIds: React.Dispatch<React.SetStateAction<string[]>>;
    onNavigate: (view: View, payload: object) => void;
    userSettings?: UserSettings;
    onShareClick?: (id: string, name?: string, item?: any) => void;
    onCopyLink?: (item: any) => void;
} & Pick<ListProps<Document>, 'onDeleteClick' | 'onEditClick'>> = ({ files, selectedDocIds, setSelectedDocIds, onNavigate, onDeleteClick, onEditClick, onShareClick, onCopyLink, userSettings }) => {
    
    const handleSelect = (docId: string) => {
        // FIX: Correctly add/remove docId to/from the array.
        setSelectedDocIds(prev =>
            prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
        );
    };
    
    if (!Array.isArray(files) || files.length === 0) {
        return <NoItemsMessage message="Chưa có tệp nào được tải lên." />;
    }

    return (
        <ul className="divide-y divide-gray-200">
            {files.map(doc => (
                <li key={doc.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                    <div className="flex items-center space-x-4">
                        <input
                            type="checkbox"
                            checked={selectedDocIds.includes(doc.id)}
                            onChange={() => handleSelect(doc.id)}
                            className="h-5 w-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <div>
                            <div className="flex items-center space-x-2">
                               <p className="font-medium text-gray-800" title={`Tài liệu: ${doc.name}`}>{doc.name}</p>
                               <VisibilityBadge visibility={(doc as any).visibility} isSharedWithMe={(doc as any).sharedWithMe} />
                               {doc.status === 'analyzing' && (
                                   <div className="flex items-center space-x-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full cursor-help border border-blue-100" title="Đang trong quá trình AI phân tích nội dung">
                                       <ArrowPathIcon className="h-3 w-3 animate-spin" />
                                   </div>
                               )}
                               {(doc.segments && doc.segments.length > 0 || doc.status === 'ready') ? (
                                   <CheckBadgeIcon className="h-5 w-5 text-green-500 flex-shrink-0" title="Đã được AI phân tích & tối ưu nội dung" />
                               ) : (
                                   doc.status !== 'analyzing' && (
                                       <div className="flex items-center space-x-1 px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full cursor-help border border-orange-100" title="Tài liệu thô - Nhấn 'Phân tích & tạo' để AI bóc tách nội dung">
                                           <ArrowPathIcon className="h-3 w-3" />
                                       </div>
                                   )
                               )}
                               {doc.sourceDocumentIds && doc.sourceDocumentIds.length > 0 && (
                                    <Square2StackIcon className="h-5 w-5 text-blue-500 flex-shrink-0" title={`Tổng hợp từ ${doc.sourceDocumentIds.length} tài liệu`} />
                               )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                                <FileTypeBadge type={doc.type} />
                                <p className="text-[11px] text-slate-400 font-medium italic">Tải lên ngày: {new Date(doc.uploadDate).toLocaleString('vi-VN')}</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button 
                            onClick={() => onNavigate(View.DocumentWorkspace, { id: doc.id, name: doc.name })}
                            className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
                        >
                            <SparklesIcon className="h-4 w-4" />
                            <span>{doc.status === 'analyzing' ? 'Tạo nội dung' : (doc.segments && doc.segments.length > 0 ? 'Tạo nội dung' : 'Phân tích & tạo')}</span>
                        </button>
                         <ActionButtons 
                            onEditClick={() => onEditClick(doc.id, doc.name)} 
                            onDeleteClick={() => onDeleteClick(doc.id, doc.name)} 
                            onShareClick={onShareClick ? () => onShareClick(doc.id, doc.name, doc) : undefined}
                            onCopyLink={((doc as any).share_token || (doc as any).shareToken) ? () => onCopyLink?.(doc) : undefined}
                         />
                    </div>
                </li>
            ))}
        </ul>
    );
};


const LessonPlansList: React.FC<{ plans: LessonPlan[]; onNavigate: (view: View, payload: object) => void; userSettings?: UserSettings; onShareClick?: (id: string, name?: string, item?: any) => void; onCopyLink?: (item: any) => void; } & ListProps<LessonPlan>> = ({ plans, onNavigate, onDeleteClick, onEditClick, onShareClick, onCopyLink, userSettings }) => {
    if (!Array.isArray(plans) || plans.length === 0) return <NoItemsMessage message="Chưa có giáo án nào." />;
    return (
        <ul className="divide-y divide-gray-200">
            {plans.map(plan => (
                <li key={plan.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                    <div className="flex items-center space-x-4">
                        <DocumentTextIcon className="h-8 w-8 text-blue-500" />
                        <div>
                            <div className="flex items-center space-x-2">
                                <p className="font-medium text-gray-800" title={`Giáo án: ${plan.title}`}>{plan.title}</p>
                                <VisibilityBadge visibility={(plan as any).visibility} isSharedWithMe={(plan as any).sharedWithMe} />
                            </div>
                            <div className="flex items-center gap-2">
                                <p className="text-sm text-gray-500">{plan.gradeLevel} - {plan.subject} | {plan.model} | Lưu ngày: {new Date(plan.savedDate).toLocaleString('vi-VN')}</p>
                                {(plan as any).target_roles && (
                                    <div className="flex gap-1">
                                        {(Array.isArray((plan as any).target_roles) ? (plan as any).target_roles : [(plan as any).target_roles]).map((r: string) => (
                                            <span key={r} title={r} className="text-xs grayscale opacity-50">
                                                {r === 'all' ? '🌟' : r === 'teacher' ? '👨‍🏫' : r === 'student' ? '🎓' : r === 'parent' ? '👪' : '📄'}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="scale-90 origin-right">
                            <DownloadButtons lessonPlan={plan} userSettings={userSettings} title={plan.title} />
                        </div>
                        <ActionButtons 
                            onEditClick={() => onNavigate(View.LessonPlanEdit, { id: plan.id, topic: plan.title })} 
                            onDeleteClick={() => onDeleteClick(plan.id, plan.title)} 
                            onShareClick={onShareClick ? () => onShareClick(plan.id, plan.title, plan) : undefined} 
                            onCopyLink={((plan as any).share_token || (plan as any).shareToken) ? () => onCopyLink?.(plan) : undefined}
                            editTitle='Sửa Giáo án'
                        >
                            <button onClick={() => onNavigate(View.SlideCreator, { sourceLessonPlan: plan })} title="Tạo trình chiếu" className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-gray-100 rounded-md">
                                <PresentationChartBarIcon className="h-5 w-5" />
                            </button>
                            <button onClick={() => onNavigate(View.QuizCreator, { sourceLessonPlan: plan })} title="Tạo bài kiểm tra" className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-gray-100 rounded-md">
                                <QuestionMarkCircleIcon className="h-5 w-5" />
                            </button>
                        </ActionButtons>
                    </div>
                </li>
            ))}
        </ul>
    );
};

const PresentationsList: React.FC<{ presentations: Presentation[]; onNavigate: (view: View, payload: object) => void; userSettings?: UserSettings; onShareClick?: (id: string, name?: string, item?: any) => void; onCopyLink?: (item: any) => void; } & ListProps<Presentation>> = ({ presentations, onNavigate, onDeleteClick, onEditClick, onShareClick, onCopyLink, userSettings }) => {
    if (!Array.isArray(presentations) || presentations.length === 0) return <NoItemsMessage message="Chưa có bài trình chiếu nào." />;
    return (
    <ul className="divide-y divide-gray-200">
        {presentations.map(pres => (
            <li key={pres.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                <div className="flex items-center space-x-4">
                    <PresentationChartBarIcon className="h-8 w-8 text-green-500" />
                    <div>
                        <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-800" title={`Trình chiếu: ${pres.title}`}>{pres.title}</p>
                            <VisibilityBadge visibility={(pres as any).visibility} isSharedWithMe={(pres as any).sharedWithMe} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-500">Số slide: {Array.isArray(pres.slides) ? pres.slides.length : 0} | Lưu ngày: {new Date(pres.savedDate).toLocaleString('vi-VN')}</p>
                            {(pres as any).target_roles && (
                                <div className="flex gap-1">
                                    {(Array.isArray((pres as any).target_roles) ? (pres as any).target_roles : [(pres as any).target_roles]).map((r: string) => (
                                        <span key={r} title={r} className="text-xs grayscale opacity-50">
                                            {r === 'all' ? '🌟' : r === 'teacher' ? '👨‍🏫' : r === 'student' ? '🎓' : r === 'parent' ? '👪' : '📄'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <div className="scale-90 origin-right">
                        <DownloadButtons presentation={pres} userSettings={userSettings} title={pres.title} />
                    </div>
                    <ActionButtons 
                        onEditClick={() => onNavigate(View.SlideEdit, { id: pres.id, title: pres.title })} 
                        onDeleteClick={() => onDeleteClick(pres.id, pres.title)} 
                        onShareClick={onShareClick ? () => onShareClick(pres.id, pres.title, pres) : undefined} 
                        onCopyLink={((pres as any).share_token || (pres as any).shareToken) ? () => onCopyLink?.(pres) : undefined}
                        editTitle='Sửa Trình chiếu'
                    >
                        <button onClick={() => onNavigate(View.MasterSlideCreator, { id: pres.id })} title="Tạo slide tổng hợp" className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-md">
                            <Square2StackIcon className="h-5 w-5" />
                        </button>
                    </ActionButtons>
                </div>
            </li>
        ))}
    </ul>
)};

const ScientificSimulationsList: React.FC<{ simulations: ScientificSimulation[]; onNavigate: (view: View, payload: object) => void; onShareClick?: (id: string, name?: string, item?: any) => void; onCopyLink?: (item: any) => void; } & ListProps<ScientificSimulation>> = ({ simulations, onNavigate, onDeleteClick, onShareClick, onCopyLink }) => {
    if (!Array.isArray(simulations) || simulations.length === 0) return <NoItemsMessage message="Chưa có mô phỏng khoa học nào." />;
    return (
    <ul className="divide-y divide-gray-200">
        {simulations.map(sim => {
            const simId = String(sim.id);
            const savedAt = sim.updatedAt || sim.updated_at || sim.createdAt || sim.created_at;
            const variableCount = Array.isArray(sim.data?.variables) ? sim.data.variables.length : 0;
            const formulaCount = Array.isArray(sim.data?.formulas) ? sim.data.formulas.length : 0;
            const topic = sim.topic || 'Mô phỏng khoa học';
            return (
                <li key={simId} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                    <div className="flex items-center space-x-4">
                        <BoltIcon className="h-8 w-8 text-teal-500" />
                        <div>
                            <div className="flex items-center space-x-2">
                                <p className="font-medium text-gray-800" title={`Mô phỏng khoa học: ${topic}`}>{topic}</p>
                                <VisibilityBadge visibility={(sim as any).visibility} isSharedWithMe={(sim as any).sharedWithMe} />
                            </div>
                            <div className="flex items-center gap-2">
                                <p className="text-sm text-gray-500">
                                    {variableCount} biến | {formulaCount} công thức | Lưu ngày: {savedAt ? new Date(savedAt).toLocaleString('vi-VN') : '-'}
                                </p>
                                {(sim as any).target_roles && (
                                    <div className="flex gap-1">
                                        {(Array.isArray((sim as any).target_roles) ? (sim as any).target_roles : [(sim as any).target_roles]).map((r: string) => (
                                            <span key={r} title={r} className="text-xs grayscale opacity-50">
                                                {r === 'all' ? '🌟' : r === 'teacher' ? '👨‍🏫' : r === 'student' ? '🎓' : r === 'parent' ? '👪' : '📄'}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <ActionButtons
                            onViewClick={() => onNavigate(View.ScientificSimulationView, { id: sim.id, title: topic })}
                            onEditClick={() => onNavigate(View.ScientificSimulationEdit, { id: sim.id, title: topic })}
                            onDeleteClick={() => onDeleteClick(simId, topic)}
                            onShareClick={onShareClick ? () => onShareClick(simId, topic, sim) : undefined}
                            onCopyLink={((sim as any).share_token || (sim as any).shareToken) ? () => onCopyLink?.(sim) : undefined}
                            editTitle='Sửa Mô phỏng'
                        />
                    </div>
                </li>
            );
        })}
    </ul>
)};

const QuizzesList: React.FC<{
    quizzes: Quiz[];
    oralQuizzes: OralQuiz[];
    filter: 'all' | 'quiz' | 'knowledge';
    onNavigate: (view: View, payload: object) => void;
    userSettings?: UserSettings;
    onShareClick?: (id: string, name?: string, item?: any) => void;
    onCopyLink?: (item: any) => void;
    onEditOralQuizClick: (id: string, name: string | undefined) => void;
    onDeleteOralQuizClick: (id: string, name: string | undefined) => void;
} & ListProps<Quiz>> = ({ quizzes, oralQuizzes, filter, onNavigate, onDeleteClick, onEditClick, onShareClick, onCopyLink, userSettings, onEditOralQuizClick, onDeleteOralQuizClick }) => {
    const items = [
        ...(filter !== 'knowledge' ? quizzes.map(quiz => ({ kind: 'quiz' as const, savedAt: quiz.savedDate, item: quiz })) : []),
        ...(filter !== 'quiz' ? oralQuizzes.map(quiz => ({ kind: 'oralQuiz' as const, savedAt: quiz.savedDate, item: quiz })) : []),
    ].sort((a, b) => new Date(b.savedAt || 0).getTime() - new Date(a.savedAt || 0).getTime());

    if (items.length === 0) {
        const message = filter === 'knowledge'
            ? 'Chưa có bài kiểm tra kiến thức nào.'
            : filter === 'quiz'
                ? 'Chưa có đề kiểm tra nào.'
                : 'Chưa có tài nguyên kiểm tra nào.';
        return <NoItemsMessage message={message} />;
    }
    return (
    <ul className="divide-y divide-gray-200">
        {items.map(({ kind, item }) => (
            <li key={`${kind}-${item.id}`} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                <div className="flex items-center space-x-4">
                    {kind === 'quiz'
                        ? <QuestionMarkCircleIcon className="h-8 w-8 text-purple-500" />
                        : <SparklesIcon className="h-8 w-8 text-indigo-500" />}
                    <div>
                        <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-800" title={`${kind === 'quiz' ? 'Bài kiểm tra' : 'Kiểm tra kiến thức'}: ${item.title}`}>{item.title}</p>
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${kind === 'quiz' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                                {kind === 'quiz' ? 'Đề kiểm tra' : 'Kiểm tra kiến thức'}
                            </span>
                            {kind === 'quiz' && <VisibilityBadge visibility={(item as any).visibility} isSharedWithMe={(item as any).sharedWithMe} />}
                        </div>
                        <div className="flex items-center gap-2">
                            {kind === 'quiz' ? (
                                <p className="text-sm text-gray-500">{(item as Quiz).gradeLevel} - {(item as Quiz).subject} | {Array.isArray((item as Quiz).questions) ? (item as Quiz).questions.length : 0} câu hỏi | Lưu ngày: {new Date(item.savedDate).toLocaleString('vi-VN')}</p>
                            ) : (
                                <p className="text-sm text-gray-500">
                                    {(item as OralQuiz).gradeLevel} - {(item as OralQuiz).subject} | {(item as OralQuiz).config?.estimatedStudentCount || 0} học sinh | {(item as OralQuiz).config?.questionsPerStudent || 0} câu/em | Lưu ngày: {new Date(item.savedDate).toLocaleString('vi-VN')}
                                </p>
                            )}
                            {kind === 'quiz' && (item as any).target_roles && (
                                <div className="flex gap-1">
                                    {(Array.isArray((item as any).target_roles) ? (item as any).target_roles : [(item as any).target_roles]).map((r: string) => (
                                        <span key={r} title={r} className="text-xs grayscale opacity-50">
                                            {r === 'all' ? '🌟' : r === 'teacher' ? '👨‍🏫' : r === 'student' ? '🎓' : r === 'parent' ? '👪' : '📄'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    {kind === 'quiz' && (
                        <div className="scale-90 origin-right">
                            <DownloadButtons quiz={item as Quiz} userSettings={userSettings} title={item.title} />
                        </div>
                    )}
                    <ActionButtons 
                        onEditClick={kind === 'quiz'
                            ? ((item as any).sharedWithMe ? undefined : () => {
                                const quiz = item as Quiz;
                                if ((quiz as any).currentStep === 4) {
                                    onNavigate(View.QuizView, { id: quiz.id, title: quiz.title });
                                } else {
                                    onNavigate(View.QuizEdit, { id: quiz.id, title: quiz.title, step: (quiz as any).currentStep || 0 });
                                }
                            })
                            : () => onNavigate(View.OralQuizEdit, { id: item.id, title: item.title })}
                        onViewClick={kind === 'quiz'
                            ? ((item as any).sharedWithMe ? () => onNavigate(View.QuizView, { id: item.id, title: item.title }) : undefined)
                            : () => onNavigate(View.OralQuizView, { id: item.id, title: item.title })}
                        onDeleteClick={kind === 'quiz'
                            ? ((item as any).sharedWithMe ? undefined : () => onDeleteClick(item.id, item.title))
                            : () => onDeleteOralQuizClick(item.id, item.title)}
                        onShareClick={kind === 'quiz' && !((item as any).sharedWithMe || !onShareClick) ? () => onShareClick(item.id, item.title, item) : undefined}
                        onCopyLink={kind === 'quiz' && (((item as any).share_token || (item as any).shareToken)) ? () => onCopyLink?.(item) : undefined}
                        editTitle={kind === 'quiz' ? 'Sửa Bài kiểm tra' : 'Sửa Kiểm tra kiến thức'}
                    >
                        {kind === 'oralQuiz' && (
                            <button
                                onClick={() => onNavigate(View.OralQuizRunner, { id: item.id, title: item.title })}
                                title="Kiểm tra ngay"
                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md"
                            >
                                <PlayIcon className="h-5 w-5" />
                            </button>
                        )}
                    </ActionButtons>
                </div>
            </li>
        ))}
    </ul>
)};

const GamesList: React.FC<{ games: Game[]; onNavigate: (view: View, payload: object) => void; userSettings?: UserSettings; onShareClick?: (id: string, name?: string, item?: Game) => void; onCopyLink?: (item: any) => void; } & ListProps<Game>> = ({ games, onNavigate, onDeleteClick, onEditClick, onShareClick, onCopyLink, userSettings }) => {
    if (!Array.isArray(games) || games.length === 0) return <NoItemsMessage message="Chưa có trò chơi nào." />;
    return (
    <ul className="divide-y divide-gray-200">
        {games.map(game => (
            <li key={String(game.id)} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                <div className="flex items-center space-x-4">
                    <BoltIcon className="h-8 w-8 text-orange-500" />
                    <div>
                        <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-800" title={`Trò chơi: ${typeof game.title === 'string' ? game.title : (game.title ?? '')}`}>{typeof game.title === 'string' ? game.title : (game.title ?? '')}</p>
                            <VisibilityBadge visibility={(game as any).visibility} isSharedWithMe={(game as any).sharedWithMe} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-500">{String(game.gradeLevel ?? game.grade_level ?? '')} - {String(game.gameType ?? game.game_type ?? '')} | Lưu ngày: {game.savedDate || game.saved_date ? new Date(game.savedDate || game.saved_date).toLocaleString('vi-VN') : ''}</p>
                            {(game as any).target_roles && (
                                <div className="flex gap-1">
                                    {(Array.isArray((game as any).target_roles) ? (game as any).target_roles : [(game as any).target_roles]).map((r: string) => (
                                        <span key={r} title={r} className="text-xs grayscale opacity-50">
                                            {r === 'all' ? '🌟' : r === 'teacher' ? '👨‍🏫' : r === 'student' ? '🎓' : r === 'parent' ? '👪' : '📄'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <button 
                        onClick={() => onNavigate(View.GameView, { id: game.id, title: typeof game.title === 'string' ? game.title : String(game.title ?? ''), isPlaying: true })}
                        className="flex items-center space-x-2 px-3 py-1.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <PlayIcon className="h-4 w-4" />
                        <span>Chơi ngay</span>
                    </button>
                    <ActionButtons 
                        onEditClick={() => onNavigate(View.GameEdit, { id: game.id, title: typeof game.title === 'string' ? game.title : String(game.title ?? '') })} 
                        onDeleteClick={() => onDeleteClick(game.id, typeof game.title === 'string' ? game.title : String(game.title ?? ''))} 
                        onShareClick={onShareClick ? () => onShareClick(game.id, typeof game.title === 'string' ? game.title : String(game.title ?? ''), game) : undefined} 
                        onCopyLink={((game as any).share_token || (game as any).shareToken) ? () => onCopyLink?.(game) : undefined}
                        editTitle='Sửa Trò chơi' 
                    />
                </div>
            </li>
        ))}
    </ul>
)};

const HomeworksList: React.FC<{ homeworks: Homework[]; onNavigate: (view: View, payload: object) => void; userSettings?: UserSettings; onShareClick?: (id: string, name?: string, item?: any) => void; onCopyLink?: (item: any) => void; } & ListProps<Homework>> = ({ homeworks, onNavigate, onDeleteClick, onEditClick, onShareClick, onCopyLink, userSettings }) => {
    if (!Array.isArray(homeworks) || homeworks.length === 0) return <NoItemsMessage message="Chưa có bài tập nào." />;
    return (
    <ul className="divide-y divide-gray-200">
        {homeworks.map(hw => (
            <li key={hw.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                <div className="flex items-center space-x-4">
                    <ClipboardDocumentListIcon className="h-8 w-8 text-yellow-500" />
                    <div>
                        <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-800" title={`Bài tập: ${typeof hw.title === 'string' ? hw.title : String(hw.title ?? '')}`}>{typeof hw.title === 'string' ? hw.title : String(hw.title ?? '')}</p>
                            <VisibilityBadge visibility={(hw as any).visibility} isSharedWithMe={(hw as any).sharedWithMe} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-500">{Array.isArray(hw.tasks) ? hw.tasks.length : 0} nhiệm vụ | Lưu ngày: {hw.savedDate || hw.saved_date ? new Date(hw.savedDate || hw.saved_date).toLocaleString('vi-VN') : ''}</p>
                            {(hw as any).target_roles && (
                                <div className="flex gap-1">
                                    {(Array.isArray((hw as any).target_roles) ? (hw as any).target_roles : [(hw as any).target_roles]).map((r: string) => (
                                        <span key={r} title={r} className="text-xs grayscale opacity-50">
                                            {r === 'all' ? '🌟' : r === 'teacher' ? '👨‍🏫' : r === 'student' ? '🎓' : r === 'parent' ? '👪' : '📄'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <ActionButtons 
                        onEditClick={() => onNavigate(View.HomeworkEdit, { id: hw.id, title: hw.title })} 
                        onDeleteClick={() => onDeleteClick(hw.id, hw.title)} 
                        onShareClick={onShareClick ? () => onShareClick(hw.id, hw.title, hw) : undefined} 
                        onCopyLink={((hw as any).share_token || (hw as any).shareToken) ? () => onCopyLink?.(hw) : undefined}
                        editTitle='Sửa Bài tập' 
                    />
                </div>
            </li>
        ))}
    </ul>
)};

const ActivitiesList: React.FC<{ activities: Activity[]; onNavigate: (view: View, payload: object) => void; userSettings?: UserSettings; onShareClick?: (id: string, name?: string, item?: any) => void; onCopyLink?: (item: any) => void; } & ListProps<Activity>> = ({ activities, onNavigate, onDeleteClick, onEditClick, onShareClick, onCopyLink, userSettings }) => {
    if (!Array.isArray(activities) || activities.length === 0) return <NoItemsMessage message="Chưa có hoạt động sáng tạo nào." />;
    return (
    <ul className="divide-y divide-gray-200">
        {activities.map(act => (
            <li key={act.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                <div className="flex items-center space-x-4">
                    <PuzzlePieceIcon className="h-8 w-8 text-indigo-500" />
                    <div>
                        <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-800" title={`Hoạt động: ${typeof act.title === 'string' ? act.title : String(act.title ?? '')}`}>{typeof act.title === 'string' ? act.title : String(act.title ?? '')}</p>
                            <VisibilityBadge visibility={(act as any).visibility} isSharedWithMe={(act as any).sharedWithMe} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-500">{String(act.activityType ?? act.activity_type ?? '')} | {String(act.gradeLevel ?? act.grade_level ?? '')} | Lưu ngày: {act.savedDate || act.saved_date ? new Date(act.savedDate || act.saved_date).toLocaleString('vi-VN') : ''}</p>
                            {(act as any).target_roles && (
                                <div className="flex gap-1">
                                    {(Array.isArray((act as any).target_roles) ? (act as any).target_roles : [(act as any).target_roles]).map((r: string) => (
                                        <span key={r} title={r} className="text-xs grayscale opacity-50">
                                            {r === 'all' ? '🌟' : r === 'teacher' ? '👨‍🏫' : r === 'student' ? '🎓' : r === 'parent' ? '👪' : '📄'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <ActionButtons 
                        onEditClick={() => onNavigate(View.ActivityEdit, { id: act.id, title: act.title })} 
                        onDeleteClick={() => onDeleteClick(act.id, act.title)} 
                        onShareClick={onShareClick ? () => onShareClick(act.id, act.title, act) : undefined} 
                        onCopyLink={((act as any).share_token || (act as any).shareToken) ? () => onCopyLink?.(act) : undefined}
                        editTitle='Sửa Hoạt động' 
                    />
                </div>
            </li>
        ))}
    </ul>
)};

interface SKKNListProps {
  projects: SKKNProject[];
  onNavigate: (view: View, payload: object) => void;
  onEditClick: (id: string, name: string | undefined) => void;
  onDeleteClick: (id: string, name: string | undefined) => void;
}

const SKKNList: React.FC<SKKNListProps> = ({ projects, onNavigate, onDeleteClick, onEditClick }) => {
  if (!Array.isArray(projects) || projects.length === 0) return <NoItemsMessage message="Chưa có SKKN nào." />;
  return (
    <ul className="divide-y divide-gray-200">
      {projects.map(proj => {
        const title = proj.data?.title || 'SKKN chưa đặt tên';
        const idStr = String(proj.id);
        return (
          <li key={proj.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
            <div className="flex items-center space-x-4">
              <CheckBadgeIcon className="h-8 w-8 text-amber-500" />
              <div>
                <p className="font-medium text-gray-800" title={`SKKN: ${title}`}>{title}</p>
                <p className="text-sm text-gray-500">{proj.data?.subject || ''} | {proj.data?.gradeLevel || ''} | Lưu: {(proj.savedAt || (proj as any).savedDate) ? new Date((proj.savedAt || (proj as any).savedDate) as string).toLocaleString('vi-VN') : '-'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <ActionButtons
                onViewClick={() => onNavigate(View.SKKNCreator, { id: idStr, title })}
                onEditClick={() => onEditClick(idStr, title)}
                onDeleteClick={() => onDeleteClick(idStr, title)}
                editTitle="Đổi tên SKKN"
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const ImagesList: React.FC<{
    images: ImageResource[];
    onZoom: (image: ImageResource) => void;
} & Pick<ListProps<ImageResource>, 'onDeleteClick' | 'onEditClick'>> = ({ images, onEditClick, onDeleteClick, onZoom }) => {
    if (!Array.isArray(images) || images.length === 0) {
        return <NoItemsMessage message="Chưa có hình ảnh nào trong thư viện." />;
    }
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map(image => (
                <div key={image.id} className="group relative aspect-square border rounded-lg overflow-hidden shadow-sm">
                    <img src={formatAssetUrl(image.url)} alt={image.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                            <p className="text-xs font-bold truncate" title={image.name}>{image.name}</p>
                            <p className="text-[10px] opacity-80 truncate" title={image.source}>Nguồn: {image.source}</p>
                            <p className="text-[9px] opacity-70">Lưu ngày: {new Date(image.createdAt).toLocaleString('vi-VN')}</p>
                        </div>
                        <div className="absolute top-1 right-1 flex items-center gap-1 bg-black/40 p-1 rounded-md">
                             <button 
                                onClick={() => onZoom(image)}
                                title="Phóng to"
                                className="p-1 text-white hover:bg-white/20 rounded-sm"
                            >
                                <ZoomInIcon className="h-4 w-4" /> 
                            </button>
                             <a 
                                href={formatAssetUrl(image.url)} 
                                download={`${image.name}.png`}
                                title="Tải xuống"
                                className="p-1 text-white hover:bg-white/20 rounded-sm"
                                onClick={e => e.stopPropagation()}
                            >
                                <ArrowDownTrayIcon className="h-4 w-4" /> 
                            </a>
                             <button onClick={() => onEditClick(image.id, image.name)} title="Đổi tên" className="p-1 text-white hover:bg-white/20 rounded-sm">
                                <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button onClick={() => onDeleteClick(image.id, image.name)} title="Xóa" className="p-1 text-white hover:bg-white/20 rounded-sm">
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const VideoProjectsList: React.FC<{
    videoProjects: VideoProject[];
    onNavigate: (view: View, payload: object) => void;
    userSettings?: UserSettings;
    onAssignClick?: (projectId: string, name: string) => void;
} & Pick<ListProps<VideoProject>, 'onDeleteClick' | 'onEditClick'>> = ({ videoProjects, onNavigate, onEditClick, onDeleteClick, userSettings, onAssignClick }) => {
    if (!Array.isArray(videoProjects) || videoProjects.length === 0) {
        return <NoItemsMessage message="Chưa có dự án video nào." />;
    }

    return (
        <ul className="divide-y divide-gray-200">
            {videoProjects.map(project => (
                <li key={project.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <PlayIcon className={`h-8 w-8 ${project.status === 'completed' ? 'text-teal-500' : project.status === 'processing' ? 'text-blue-500' : 'text-purple-500'}`} />
                            {project.status === 'processing' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="font-medium text-gray-800" title={`Dự án Video: ${project.name}`}>{project.name}</p>
                            <p className="text-sm text-gray-500">
                                {project.scenes && project.scenes[0] && project.scenes[0].type === 'interactive_meta' ? (
                                    <span className="flex items-center gap-1">
                                        <SparklesIcon className="h-3 w-3 text-teal-500" />
                                        Video tương tác | {project.scenes[0].interactions?.length || 0} điểm dừng
                                    </span>
                                ) : (
                                    <>
                                        {(project.scenes?.length || 0)} cảnh | 
                                    </>
                                )}
                                {project.status === 'processing' ? (
                                    <span className="text-blue-600 font-medium ml-1"> {project.progress || 'Đang xử lý...'}</span>
                                ) : project.status === 'completed' ? (
                                    <span className="text-teal-600 ml-1"> Hoàn thành</span>
                                ) : (
                                    ` Tạo ngày: ${new Date(project.createdAt || project.created_at).toLocaleString('vi-VN')}`
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        {userSettings?.role === 'teacher' && (project.scenes && project.scenes[0] && project.scenes[0].type === 'interactive_meta') && (
                            <button 
                                onClick={() => onAssignClick?.(project.id, project.name)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors"
                            >
                                <ClipboardDocumentListIcon className="h-4 w-4" />
                                <span>Giao bài</span>
                            </button>
                        )}

                        <button 
                            onClick={() => {
                                const isInteractive = project.scenes && project.scenes[0] && project.scenes[0].type === 'interactive_meta';
                                onNavigate(isInteractive ? View.InteractiveVideoEdit : View.VideoCreator, { videoProjectId: project.id, name: project.name });
                            }} 
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                project.status === 'processing' 
                                ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                : (project.scenes && project.scenes[0] && project.scenes[0].type === 'interactive_meta')
                                    ? 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'
                                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                            }`}
                        >
                            {project.status === 'processing' ? (
                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            ) : (project.scenes && project.scenes[0] && project.scenes[0].type === 'interactive_meta') ? (
                                <PencilSquareIcon className="h-4 w-4" />
                            ) : (
                                <SparklesIcon className="h-4 w-4" />
                            )}
                            <span>
                                {project.status === 'processing' 
                                    ? 'Theo dõi tiến độ' 
                                    : (project.scenes && project.scenes[0] && project.scenes[0].type === 'interactive_meta')
                                        ? 'Chỉnh sửa Video' 
                                        : 'Dựng Video'}
                            </span>
                        </button>

                        <ActionButtons onEditClick={() => onEditClick(project.id, project.name)} onDeleteClick={() => onDeleteClick(project.id, project.name)} editTitle='Đổi tên Dự án' />
                    </div>
                </li>
            ))}
        </ul>
    );
};


const VideoResourcesList: React.FC<{
    videos: VideoResource[];
    onNavigate: (view: View, payload: object) => void;
    userSettings?: UserSettings;
} & Pick<ListProps<VideoResource>, 'onDeleteClick' | 'onEditClick'>> = ({ videos, onNavigate, onEditClick, onDeleteClick, userSettings }) => {
    if (!Array.isArray(videos) || videos.length === 0) {
        return <NoItemsMessage message="Chưa có video nào đã tạo." />;
    }

    return (
        <ul className="divide-y divide-gray-200">
            {videos.map(video => (
                <li key={video.id} className="py-4 flex items-center justify-between group hover:bg-gray-50 -mx-6 px-6">
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <PlayIcon className={`h-8 w-8 ${video.status === 'completed' ? 'text-teal-500' : video.status === 'failed' ? 'text-red-500' : 'text-blue-500'}`} />
                            {video.status === 'processing' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="font-medium text-gray-800" title={`Video: ${video.name}`}>{video.name}</p>
                            <p className="text-sm text-gray-500">
                                {video.source} | {video.status === 'completed' ? 'Đã hoàn thành' : video.status === 'failed' ? 'Thất bại' : 'Đang xử lý...'} | Tạo ngày: {new Date(video.createdAt).toLocaleString('vi-VN')}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center gap-2">
                            {video.status === 'completed' && (
                                <div className="scale-90 origin-right">
                                    <DownloadButtons video={video} userSettings={userSettings} />
                                </div>
                            )}
                            
                            <button 
                                onClick={() => onNavigate(View.VideoCreator, { videoId: video.id })} 
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                    video.status === 'completed' 
                                        ? 'text-teal-700 bg-teal-50 hover:bg-teal-100' 
                                        : video.status === 'failed'
                                            ? 'text-red-700 bg-red-50 hover:bg-red-100 border border-red-200'
                                            : 'text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200'
                                }`}
                            >
                                <PlayIcon className="h-4 w-4" />
                                <span>
                                    {video.status === 'completed' ? 'Xem Video' : video.status === 'failed' ? 'Xem lỗi & Thử lại' : 'Kiểm tra tiến độ'}
                                </span>
                            </button>
                        </div>

                        <ActionButtons onEditClick={() => onEditClick(video.id, video.name)} onDeleteClick={() => onDeleteClick(video.id, video.name)} editTitle='Đổi tên Video' />
                    </div>
                </li>
            ))}
        </ul>
    );
};


export default DocumentLibrary;
